import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CFormSelect,
  CButton,
  CSpinner,
  CAlert,
  CBadge
} from '@coreui/react';
import { CChartLine } from '@coreui/react-chartjs';
import CIcon from '@coreui/icons-react';
import { cilChartLine, cilReload } from '@coreui/icons';
import { AuthUserContext } from '../../session/context';
import axios from 'axios';
import config from '../../../utils/EnvironmentSwitcher';
import { fetchAllFhirBundleEntries } from '../../../utils/fhirPagination';
import { SkeletonChart } from '../../../components/SkeletonLoaders';

// Color palette for different observation types
const CHART_COLORS = {
  'Heart Rate': { bg: 'rgba(220, 53, 69, 0.2)', border: '#dc3545' },
  'Body Temperature': { bg: 'rgba(255, 193, 7, 0.2)', border: '#ffc107' },
  'Oxygen Saturation': { bg: 'rgba(23, 162, 184, 0.2)', border: '#17a2b8' },
  'Blood Glucose': { bg: 'rgba(0, 123, 255, 0.2)', border: '#007bff' },
  'Body Weight': { bg: 'rgba(108, 117, 125, 0.2)', border: '#6c757d' },
  'Steps Taken': { bg: 'rgba(40, 167, 69, 0.2)', border: '#28a745' },
  'Sleep Duration': { bg: 'rgba(102, 16, 242, 0.2)', border: '#6610f2' },
  'Systolic BP': { bg: 'rgba(220, 53, 69, 0.2)', border: '#dc3545' },
  'Diastolic BP': { bg: 'rgba(253, 126, 20, 0.2)', border: '#fd7e14' },
  'Calories': { bg: 'rgba(255, 193, 7, 0.2)', border: '#ffc107' },
  'Water Intake': { bg: 'rgba(23, 162, 184, 0.2)', border: '#17a2b8' },
  'default': { bg: 'rgba(0, 123, 255, 0.2)', border: '#007bff' }
};

// Time range options
const TIME_RANGES = [
  { value: '7', label: 'Last 7 Days' },
  { value: '14', label: 'Last 14 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' }
];

/**
 * ObservationTrendsChart - Displays FHIR observations as a line chart over time
 */
const ObservationTrendsChart = () => {
  const { authUser } = useContext(AuthUserContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [observationTypes, setObservationTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [timeRange, setTimeRange] = useState('7');

  // Get authentication token
  const getAuthToken = useCallback(async () => {
    try {
      if (authUser?.stsTokenManager?.accessToken) {
        return authUser.stsTokenManager.accessToken;
      }
      return localStorage.getItem('mieFToken');
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }, [authUser]);

  // Fetch and process observations
  const fetchObservations = useCallback(async () => {
    if (!authUser?.uid) {
      setError('Please sign in to view trends');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const fhirEndpoint = `${config.fhirUrl}/fhir`;
      
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeRange));
      
      const queryUrl = `${fhirEndpoint}/Observation?identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}&date=ge${startDate.toISOString().split('T')[0]}&date=le${endDate.toISOString().split('T')[0]}&_sort=date&_count=100`;

      const bundleEntries = await fetchAllFhirBundleEntries(queryUrl, {
        'Content-Type': 'application/fhir+json',
        'Authorization': `Bearer ${token}`,
      }, {
        httpClient: (url, opts) => axios.get(url, { headers: opts.headers }).then((res) => ({
          ok: res.status >= 200 && res.status < 300,
          status: res.status,
          json: async () => res.data,
        })),
      });

      if (!bundleEntries.length) {
        setChartData({ labels: [], datasets: [] });
        setObservationTypes([]);
        setLoading(false);
        return;
      }

      // Process observations and group by type and date
      const observationsByType = {};
      const allDates = new Set();

      bundleEntries.forEach(entry => {
        const resource = entry.resource;
        const displayName = resource.code?.coding?.[0]?.display || 
                           resource.code?.text || 
                           'Unknown';
        const dateStr = (resource.effectiveDateTime || resource.meta?.lastUpdated).split('T')[0];
        const value = resource.valueQuantity?.value;

        if (value === undefined) return;

        allDates.add(dateStr);

        if (!observationsByType[displayName]) {
          observationsByType[displayName] = {};
        }

        // Average if multiple values on same date
        if (!observationsByType[displayName][dateStr]) {
          observationsByType[displayName][dateStr] = { sum: 0, count: 0 };
        }
        observationsByType[displayName][dateStr].sum += value;
        observationsByType[displayName][dateStr].count += 1;
      });

      // Get sorted unique dates
      const sortedDates = Array.from(allDates).sort();
      
      // Get all observation types
      const types = Object.keys(observationsByType).sort();
      setObservationTypes(types);
      
      // Auto-select first 3 types if nothing selected
      if (selectedTypes.length === 0 && types.length > 0) {
        setSelectedTypes(types.slice(0, Math.min(3, types.length)));
      }

      // Build datasets for selected types
      const datasets = types
        .filter(type => selectedTypes.length === 0 || selectedTypes.includes(type))
        .map(type => {
          const colors = CHART_COLORS[type] || CHART_COLORS.default;
          const data = sortedDates.map(date => {
            const obs = observationsByType[type][date];
            return obs ? Math.round((obs.sum / obs.count) * 100) / 100 : null;
          });

          return {
            label: type,
            data: data,
            backgroundColor: colors.bg,
            borderColor: colors.border,
            fill: true,
            tension: 0.4,
            spanGaps: true, // Connect across null values
          };
        });

      // Format dates for display
      const labels = sortedDates.map(date => {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });

      setChartData({ labels, datasets });
    } catch (err) {
      console.error('Error fetching observations for chart:', err);
      setError(err.message || 'Failed to fetch observations');
    } finally {
      setLoading(false);
    }
  }, [authUser, getAuthToken, timeRange, selectedTypes]);

  // Initial load and when dependencies change
  useEffect(() => {
    fetchObservations();
  }, [fetchObservations]);

  // Handle type selection
  const handleTypeChange = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Chart options
  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        grid: {
          drawOnChartArea: false,
        },
      },
      y: {
        beginAtZero: false,
        ticks: {
          maxTicksLimit: 8,
        },
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        radius: 4,
        hitRadius: 10,
        hoverRadius: 6,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  return (
    <CCard className="mb-4">
      <CCardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="d-flex align-items-center">
          <CIcon icon={cilChartLine} className="me-2" />
          <strong>Observation Trends</strong>
        </div>
        <div className="d-flex align-items-center gap-2">
          <CFormSelect
            size="sm"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{ width: 'auto' }}
          >
            {TIME_RANGES.map(range => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </CFormSelect>
          <CButton 
            color="primary" 
            size="sm" 
            onClick={fetchObservations}
            disabled={loading}
          >
            <CIcon icon={cilReload} className="me-1" />
            Refresh
          </CButton>
        </div>
      </CCardHeader>
      
      <CCardBody>
        {/* Type Selection Badges */}
        {observationTypes.length > 0 && (
          <div className="mb-3">
            <small className="mb-2 me-2">Select metrics:</small>
            {observationTypes.map(type => {
              const colors = CHART_COLORS[type] || CHART_COLORS.default;
              const isSelected = selectedTypes.includes(type);
              return (
                <CBadge
                  key={type}
                  color={isSelected ? 'primary' : 'light'}
                  className="me-1 mb-1"
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: isSelected ? colors.border : undefined,
                    border: `2px solid ${colors.border}`,
                    color: isSelected ? 'white' : colors.border
                  }}
                  onClick={() => handleTypeChange(type)}
                >
                  {type}
                </CBadge>
              );
            })}
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <CAlert color="danger" className="mb-3">
            {error}
          </CAlert>
        )}

        {/* Loading State - use skeleton for initial load */}
        {loading ? (
          <SkeletonChart height="350px" />
        ) : chartData.datasets.length === 0 ? (
          <CAlert color="info">
            No observation data available for the selected time range. Add observations or load demo data to see trends.
          </CAlert>
        ) : (
          <div style={{ height: '350px' }}>
            <CChartLine
              data={chartData}
              options={chartOptions}
            />
          </div>
        )}

        {/* Summary Stats */}
        {!loading && chartData.datasets.length > 0 && (
          <CRow className="mt-3">
            {chartData.datasets.slice(0, 4).map((dataset) => {
              const values = dataset.data.filter(v => v !== null);
              const latest = values[values.length - 1];
              const avg = values.length > 0 
                ? Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100 
                : 0;
              const min = values.length > 0 ? Math.min(...values) : 0;
              const max = values.length > 0 ? Math.max(...values) : 0;
              
              return (
                <CCol key={dataset.label} xs={6} md={3} className="text-center mb-2">
                  <div 
                    className="p-2 rounded" 
                    style={{ backgroundColor: dataset.backgroundColor }}
                  >
                    <small className="mb-2 d-block">{dataset.label}</small>
                    <strong style={{ color: dataset.borderColor }}>{latest ?? 'N/A'}</strong>
                    <small className="d-block mb-2">
                      Avg: {avg} | Range: {min}-{max}
                    </small>
                  </div>
                </CCol>
              );
            })}
          </CRow>
        )}
      </CCardBody>
    </CCard>
  );
};

export default ObservationTrendsChart;


